// src/pages/student/exams/WriteExam.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import durationPlugin from "dayjs/plugin/duration";
import {
  Card, Spinner, Alert, Button, Badge, ProgressBar, Form, Modal
} from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getAuth } from "firebase/auth";
import {
  doc, getDoc, setDoc, serverTimestamp, updateDoc,
  collection, getDocs, query, where, limit
} from "firebase/firestore";
import { useBrand, hexToRgba } from "../_brand";
import { db } from "../../../services/firebase";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";

dayjs.extend(durationPlugin);

const TOAST_ID = "student-exams";

const toJS  = (t) => (t?.toDate ? t.toDate() : t ? new Date(t) : null);
const norm  = (s) => String(s || "").trim().toLowerCase();
const upper = (s) => String(s || "").trim().toUpperCase();

/* ---------------- schedule helpers ---------------- */
function computeSchedule(exam) {
  if (!exam?.date || !exam?.startTime || !exam?.endTime) {
    return { valid: false, state: "unknown", start: null, end: null, leftSec: 0, totalSec: 0 };
  }
  const start = dayjs(`${exam.date} ${exam.startTime}`, "YYYY-MM-DD HH:mm", true);
  let end     = dayjs(`${exam.date} ${exam.endTime}`, "YYYY-MM-DD HH:mm", true);
  if (!start.isValid() || !end.isValid()) {
    return { valid: false, state: "unknown", start: null, end: null, leftSec: 0, totalSec: 0 };
  }
  if (end.isBefore(start)) end = start.add(exam.durationMins || 0, "minute");
  const totalSec = Math.max(0, end.diff(start, "second"));
  const now = dayjs();
  if (now.isBefore(start)) return { valid: true, state: "upcoming", start, end, leftSec: end.diff(now, "second"), totalSec };
  if (now.isAfter(end))    return { valid: true, state: "ended",   start, end, leftSec: 0, totalSec };
  return { valid: true, state: "live", start, end, leftSec: end.diff(now, "second"), totalSec };
}

/* student timer — based on attempt.startedAt; hard-capped by schedule end */
function computeStudentTimer(exam, attempt) {
  const started = toJS(attempt?.startedAt);
  if (!started) return { mode: "idle", leftSec: 0, totalSec: 0, label: "" };
  if (attempt?.status === "submitted") return { mode: "submitted", leftSec: 0, totalSec: 0, label: "Completed" };

  const isMock = norm(exam?.examMode) === "mock";
  const dur = Number(exam?.durationMins ?? 0);
  const unlimited = isMock || !Number.isFinite(dur) || dur <= 0;
  if (unlimited) return { mode: "anytime", leftSec: 0, totalSec: 0, label: "Anytime" };

  const personalEnd = dayjs(started).add(dur, "minute");
  const sched = computeSchedule(exam);
  const hardEnd = sched?.end && personalEnd.isAfter(sched.end) ? sched.end : personalEnd;

  const now = dayjs();
  const totalSec = Math.max(0, hardEnd.diff(started, "second"));
  const leftSec  = Math.max(0, hardEnd.diff(now, "second"));
  if (leftSec <= 0) return { mode: "time-up", leftSec: 0, totalSec, label: "Time up" };

  const d = dayjs.duration(leftSec, "seconds");
  const hh = String(Math.floor(d.asHours())).padStart(2, "0");
  const mm = String(d.minutes()).padStart(2, "0");
  const ss = String(d.seconds()).padStart(2, "0");
  return { mode: "running", leftSec, totalSec, label: `${hh}:${mm}:${ss}` };
}

/* debouncer */
function useDebouncedCallback(fn, delay) {
  const t = useRef(null);
  const fnRef = useRef(fn);
  useEffect(() => { fnRef.current = fn; }, [fn]);
  return useCallback((...args) => {
    if (t.current) clearTimeout(t.current);
    t.current = setTimeout(() => fnRef.current(...args), delay);
  }, [delay]);
}

/* shuffle deterministic */
function mulberry32(seed) {
  return function () {
    let t = (seed += 0x6D2B79F5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function shuffleStable(arr, seedStr) {
  const seed = [...String(seedStr)].reduce((a, c) => (a * 31 + c.charCodeAt(0)) >>> 0, 0) || 1;
  const rand = mulberry32(seed);
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/* option helpers */
const optText  = (opt,i)=> typeof opt==="object" ? (opt?.value ?? opt?.html ?? opt?.text ?? `Option ${i+1}`) : (opt ?? `Option ${i+1}`);
const optValue = (opt,i)=> typeof opt==="object" ? (opt?.key ?? opt?.valueId ?? i) : (opt ?? i);

/* match helpers */
const leftLabel  = (idx)=>{ let n=idx+1,s=""; while(n>0){ n--; s=String.fromCharCode(97+(n%26))+s; n=Math.floor(n/26);} return s;};
const toRoman    = (num)=>{ const map=[[1000,"m"],[900,"cm"],[500,"d"],[400,"cd"],[100,"c"],[90,"xc"],[50,"l"],[40,"xl"],[10,"x"],[9,"ix"],[5,"v"],[4,"iv"],[1,"i"]]; let res="",n=Math.max(1,Math.floor(num)); for(const [v,s] of map) while(n>=v){res+=s;n-=v;} return res;};
const rightLabel = (idx)=>toRoman(idx+1);
const formatMappingText = (arr=[]) => (!arr?.length || arr.some(v=>v==null)) ? "—" : arr.map((rIdx,lIdx)=>`(${leftLabel(lIdx)})-(${rightLabel(rIdx)})`).join(", ");

/* ---------------- component ---------------- */
export default function WriteExam() {
  const { examId } = useParams();
  const nav = useNavigate();
  const brand = useBrand();

  // THEME
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem("examTheme");
    if (saved === "light" || saved === "dark") return saved;
    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) return "dark";
    return "light";
  });
  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    localStorage.setItem("examTheme", next);
  };

  // session
  const [schoolId]    = useState(() => localStorage.getItem("schoolId"));
  const [studentId]   = useState(() => localStorage.getItem("studentId"));
  const [studentName] = useState(() => localStorage.getItem("studentName") || "");

  // auth
  const auth = getAuth();
  const uid  = auth.currentUser?.uid || null;

  // profile + exam
  const [profile, setProfile] = useState(null);
  const [exam, setExam]       = useState(null);
  const [loading, setLoading] = useState(true);

  // questions + answers + meta
  const [questions, setQuestions] = useState([]);
  const [qIndex, setQIndex]       = useState(0);
  const [answers, setAnswers]     = useState({});
  const [visited, setVisited]     = useState(new Set());
  const [flags, setFlags]         = useState(new Set());
  const [saving, setSaving]       = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // per-question time (seconds)
  const [perQSec, setPerQSec] = useState({});
  const qTimerRef = useRef({ qid: null, startedAt: null });

  // attempt
  const [starting, setStarting] = useState(false);
  const [attempt, setAttempt]   = useState(null);

  // live tick
  const [tick, setTick] = useState(0);
  useEffect(() => { const id = setInterval(() => setTick(t => t + 1), 1000); return () => clearInterval(id); }, []);

  // UI: question map toggle
  const [showMap, setShowMap] = useState(false);

  // full-screen mode
  useEffect(() => { document.body.classList.add("exam-mode"); return () => document.body.classList.remove("exam-mode"); }, []);

  // online
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  // security
  const tabSwitchCountRef = useRef(0);
  const MAX_TAB_SWITCHES  = 5;
  const [locked, setLocked] = useState(false);
  const [screenshotShield, setScreenshotShield] = useState(false);
  const isLiveExam = useMemo(() => norm(exam?.examMode) === "live", [exam]);
  const blockKeys = useCallback((e) => {
    if (!attempt || norm(attempt.status) !== "in-progress"|| !isLiveExam) return;
    const ctrl = e.ctrlKey || e.metaKey;
    const key  = e.key?.toLowerCase();

    if (ctrl && ["c","x","a","s","p","u"].includes(key)) {
      e.preventDefault();
      toast.warn("Action disabled during the exam.", { containerId: TOAST_ID, autoClose: 1200 });
    }
    if (e.key === "PrintScreen" || e.keyCode === 44) {
      setScreenshotShield(true);
      setTimeout(() => setScreenshotShield(false), 1800);
      e.preventDefault();
      Swal.fire({ icon: "warning", title: "Screenshots are not allowed", timer: 1500, showConfirmButton: false });
    }
    if (key === "f12" || (ctrl && e.shiftKey && ["i","j","c"].includes(key))) {
      e.preventDefault();
      toast.warn("Developer tools are disabled during the exam.", { containerId: TOAST_ID, autoClose: 1200 });
    }

    if (key === "arrowleft")  goto(qIndex - 1);
    if (key === "arrowright") goto(qIndex + 1);
    if (key === "f") toggleFlag();
    if (key === "c") clearCurrent();
  }, [attempt, qIndex,isLiveExam]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const stopDrag = (ev) => ev.preventDefault();
    document.addEventListener("dragstart", stopDrag, true);
    return () => document.removeEventListener("dragstart", stopDrag, true);
  }, []);

  const blockContext   = useCallback((e)=>{ if (attempt && norm(attempt.status)==="in-progress" && isLiveExam) { e.preventDefault(); toast.warn("Right-click disabled.", { containerId: TOAST_ID, autoClose: 1200 }); }}, [attempt]);
  const blockCopyPaste = useCallback((e)=>{ if (attempt && norm(attempt.status)==="in-progress" && isLiveExam) { e.preventDefault(); toast.warn("Copy/Paste disabled.", { containerId: TOAST_ID, autoClose: 1200 }); }}, [attempt]);

  /* ----- leave/Back control ----- */
  const autosaveEnabledRef = useRef(true);
  const leavingRef = useRef(false);
  const popHandlerRef = useRef(null);
  const backPressCountRef = useRef(0);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      if (leavingRef.current) return;
      if (attempt && norm(attempt.status)==="in-progress") { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [attempt]);

  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const onPop = () => {
      if (attempt && norm(attempt.status)==="in-progress") {
        window.history.pushState(null, "", window.location.href);
        backPressCountRef.current += 1;
        setShowLeaveConfirm(true);
        if (backPressCountRef.current >= 3) {
          toast.warn("You pressed back multiple times. Leaving will discard the current session.", { containerId: TOAST_ID });
        }
      }
    };
    popHandlerRef.current = onPop;
    window.addEventListener("popstate", onPop);
    return () => { if (popHandlerRef.current) window.removeEventListener("popstate", popHandlerRef.current); };
  }, [attempt]);

  useEffect(() => {
    const active = attempt && norm(attempt.status)==="in-progress" && isLiveExam;
    if (!active) return;
    window.addEventListener("keydown", blockKeys, { capture: true });
    window.addEventListener("contextmenu", blockContext, { capture: true });
    window.addEventListener("copy", blockCopyPaste, { capture: true });
    window.addEventListener("cut", blockCopyPaste, { capture: true });
    window.addEventListener("paste", blockCopyPaste, { capture: true });
    return () => {
      window.removeEventListener("keydown", blockKeys, { capture: true });
      window.removeEventListener("contextmenu", blockContext, { capture: true });
      window.removeEventListener("copy", blockCopyPaste, { capture: true });
      window.removeEventListener("cut", blockCopyPaste, { capture: true });
      window.removeEventListener("paste", blockCopyPaste, { capture: true });
    };
  }, [attempt, blockKeys, blockContext, blockCopyPaste, isLiveExam]);

  /* -------- Tab-switch monitor + lock after limit -------- */
  useEffect(() => {
    if (!attempt || norm(attempt.status) !== "in-progress" || !isLiveExam) return;

    const recordSwitch = async () => {
      if (locked) return;
      tabSwitchCountRef.current += 1;
      const count = tabSwitchCountRef.current;

      try {
        await updateDoc(doc(db, "schools", schoolId, "exams", examId, "attempts", uid), {
          tabSwitchCount: count, lastHeartbeatAt: serverTimestamp()
        });
      } catch {}

      if (count >= MAX_TAB_SWITCHES) {
        await Swal.fire({
          icon: "error",
          title: "Exam locked",
          html: `You switched tabs more than ${MAX_TAB_SWITCHES} times.`,
          confirmButtonText: "OK",
          allowOutsideClick: false,
          allowEscapeKey: false,
        });
        await lockExam("tab-switch-limit");
        return;
      }

      await Swal.fire({
        icon: "warning",
        title: "Stay on the exam tab",
        html: `Tab switches: <b>${count}</b> / <b>${MAX_TAB_SWITCHES}</b>. Further switches may lock your exam.`,
        timer: 1800,
        showConfirmButton: false,
      });
    };

    const onBlur = () => recordSwitch();
    const onVisibility = () => { if (document.hidden) recordSwitch(); };

    window.addEventListener("blur", onBlur);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [attempt, locked, examId, schoolId, uid, isLiveExam]);

  /* ---------------- load profile + exam + questions ---------------- */
  useEffect(() => {
    const load = async () => {
      try {
        if (!schoolId || !studentId || !examId) {
          toast.error("Missing context. Please sign in again.", { containerId: TOAST_ID });
          return;
        }
        const profRef = doc(db,"schools",schoolId,"students",studentId);
        const profSnap= await getDoc(profRef);
        if (!profSnap.exists()) { toast.error("Could not find your profile.", { containerId: TOAST_ID }); setLoading(false); return; }
        const prof = { id: profSnap.id, ...profSnap.data() };
        setProfile(prof);

        const examRef = doc(db,"schools",schoolId,"exams",examId);
        const examSnap= await getDoc(examRef);
        if (!examSnap.exists()) { toast.error("Exam not found.", { containerId: TOAST_ID }); setLoading(false); return; }
        const e = { id: examSnap.id, ...examSnap.data() };
        setExam(e);

        let qs = [];
        if (e.templateId) {
          const qCol   = collection(db,"schools",schoolId,"questions");
          const qQuery = query(qCol, where("templateId","==",e.templateId), limit(800));
          const qSnap  = await getDocs(qQuery);
          qs = qSnap.docs.map(d=>({ id:d.id, ...d.data() }));
          const ord = (q)=> q.order ?? q.index ?? q.number ?? q.seq ?? 0;
          qs.sort((a,b)=>{ const oa=ord(a), ob=ord(b);
            if (oa!==ob) return oa-ob;
            const ca=toJS(a.createdAt)?.getTime()||0, cb=toJS(b.createdAt)?.getTime()||0;
            if (ca!==cb) return ca-cb; return String(a.id).localeCompare(String(b.id));
          });
          if (e.shuffleQuestions === true) qs = shuffleStable(qs, `${examId}:${uid || ""}`);
        }
        setQuestions(qs);

        if (uid) {
          
          const attRef = doc(db,"schools",schoolId,"exams",examId,"attempts",uid);
          const attSnap= await getDoc(attRef);
          if (attSnap.exists()) {
            const att = { id: attSnap.id, ...attSnap.data() };
            setAttempt(att);
            setLocked(Boolean(att?.proctor?.locked) || norm(att?.status) === "locked");
            if (att.answers && typeof att.answers === "object") setAnswers(att.answers);
            if (att.flags && Array.isArray(att.flags)) setFlags(new Set(att.flags));
            if (att.visited && Array.isArray(att.visited)) setVisited(new Set(att.visited));
            if (att.perQuestionSec && typeof att.perQuestionSec === "object") setPerQSec(att.perQuestionSec);
            if (Number.isFinite(att?.tabSwitchCount)) tabSwitchCountRef.current = Number(att.tabSwitchCount) || 0;
          }
        }
      } catch (err) {
        console.error(err);
        toast.error(`Failed to load exam. ${err.code || ""} ${err.message || ""}`, { containerId: TOAST_ID });
      } finally { setLoading(false); }
    };
    load();
  }, [schoolId, studentId, examId, uid]);

  /* -------------- eligibility / timing -------------- */
  const isActive = norm(profile?.status) !== "inactive";
  const matchesSection = (e, cls, sec) => {
    const SEC = upper(sec || "");
    if (Array.isArray(e.targets) && e.targets.length) {
      return e.targets.some((t) => t.classId === cls && (upper(t.sectionKey || "") === SEC || (t.sectionKey || "") === ""));
    }
    const map = e.sectionKeysByClass;
    if (map && typeof map === "object" && Array.isArray(map[cls])) return map[cls].map(upper).includes(SEC);
    if (Array.isArray(e.classIds) && e.classIds.includes(cls)) return true;
    return false;
  };
  const sched = useMemo(() => computeSchedule(exam), [exam, tick]);

  // ✅ Allow mock exams anytime; live exams only during live window
  const eligible = useMemo(() => {
    if (!exam || !profile || !isActive || !exam.isPublished) return false;
    const inWindow = norm(exam?.examMode) === "mock" ? true : sched.state === "live";
    return inWindow && matchesSection(exam, profile.className, profile.section);
  }, [exam, profile, isActive, sched.state]);

  /* -------------- start / submit / autosave -------------- */
  const [showConfirm, setShowConfirm] = useState(false);

  const beginAttempt = async () => {
    if (!uid)        { toast.error("You are not signed in.", { containerId: TOAST_ID }); return; }
    if (!eligible)   { toast.error("You are not eligible to start this exam now.", { containerId: TOAST_ID }); return; }
    if (locked)      { toast.error("This exam is locked for your account.", { containerId: TOAST_ID }); return; }
    try {
      setStarting(true);
      tabSwitchCountRef.current = 0;
      const attRef = doc(db,"schools",schoolId,"exams",examId,"attempts",uid);
      await setDoc(attRef, {
        uid, studentId,
        byStudent: { name: profile?.name || profile?.displayName || studentName || "", rollNo: profile?.rollNo || "" },
        className: profile?.className || "", section: profile?.section || "",
        startedAt: serverTimestamp(), status: "in-progress",
        tabSwitchCount: tabSwitchCountRef.current || 0, lastHeartbeatAt: serverTimestamp(),
        answers: {}, flags: [], visited: [], perQuestionSec: {}
      }, { merge: true });
      const attSnap = await getDoc(attRef);
      setAttempt(attSnap.exists() ? { id: attSnap.id, ...attSnap.data() } : { status: "in-progress", answers: {} });
      toast.success("Exam started. Good luck!", { containerId: TOAST_ID });
      startQTimer(questions[0]?.id || null);
    } catch (err) {
      console.error(err);
      toast.error(`Failed to start the exam. ${err.code || ""} ${err.message || ""}`, { containerId: TOAST_ID });
    } finally { setStarting(false); }
  };

  // heartbeat, auto-end, time alerts
  const lastAlertRef = useRef({});
  useEffect(() => {
    if (!attempt || norm(attempt.status) !== "in-progress" || !exam) return;
    let hbId = null, endCheckId = null;
    const heartbeat = async () => {
      try { await updateDoc(doc(db,"schools",schoolId,"exams",examId,"attempts",uid), { lastHeartbeatAt: serverTimestamp() }); } catch {}
    };
    hbId = window.setInterval(heartbeat, 20000);

    const endCheck = async () => {
      const isMock = norm(exam?.examMode) === "mock";
      const st = computeStudentTimer(exam, attempt);

      if (!isMock && st.mode === "running") {
        const minsLeft = Math.ceil(st.leftSec / 60);
        for (const m of [30, 15, 5, 1]) {
          if (minsLeft === m && !lastAlertRef.current[m]) {
            lastAlertRef.current[m] = true;
            toast.info(`${m} minute${m>1?"s":""} remaining.`, { containerId: TOAST_ID });
          }
        }
      }

      if (!isMock) {
        if (st.mode === "time-up") { toast.info("Time is up. Submitting…", { containerId: TOAST_ID }); await autoSubmit("time-up"); }
        const sc = computeSchedule(exam);
        if (sc.state !== "live") { toast.info("Exam window closed. Submitting…", { containerId: TOAST_ID }); await autoSubmit("window-closed"); }
      }
    };
    endCheckId = window.setInterval(endCheck, 1000);

    return () => { if (hbId) clearInterval(hbId); if (endCheckId) clearInterval(endCheckId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt, exam]);

  const autoSubmit = async (reason = "manual") => {
    stopQTimer();
    if (locked) return;
    try {
      setLocked(true);
      const ref = doc(db,"schools",schoolId,"exams",examId,"attempts",uid);
      await setDoc(ref, {
        status: "submitted",
        submittedAt: serverTimestamp(),
        submitReason: reason,
        lastSavedAt: serverTimestamp(),
        answers,
        flags: Array.from(flags),
        visited: Array.from(visited),
        perQuestionSec: perQSec,
        answeredCount: Object.values(answers || {}).filter(a => (Array.isArray(a.value) ? a.value.length : String(a.value || "").trim().length)).length,
      }, { merge: true });
      const snap = await getDoc(ref);
      setAttempt(snap.exists() ? { id: snap.id, ...snap.data() } : { status: "submitted" });
      toast.success("Submitted.", { containerId: TOAST_ID });
      setTimeout(() => nav(`/student/exams/${examId}/results`), 900);
    } catch (err) {
      console.error(err);
      setLocked(false);
      toast.error(`Failed to submit. ${err.code || ""} ${err.message || ""}`, { containerId: TOAST_ID });
    }
  };

  const lockExam = async (reason = "policy-violation") => {
    if (locked) return;
    try {
      setLocked(true);
      autosaveEnabledRef.current = false;
      stopQTimer();

      const ref = doc(db, "schools", schoolId, "exams", examId, "attempts", uid);
      await setDoc(ref, {
        status: "locked",
        proctor: { locked: true, reason },
        lockedAt: serverTimestamp(),
        lastSavedAt: serverTimestamp(),
        answers,
        flags: Array.from(flags),
        visited: Array.from(visited),
        perQuestionSec: perQSec,
      }, { merge: true });

      await Swal.fire({
        icon: "error",
        title: "Exam locked",
        html: "You switched tabs multiple times. The exam is now locked.",
        confirmButtonText: "OK",
        allowOutsideClick: false,
        allowEscapeKey: false,
      });

      nav("/student/exams");
    } catch (err) {
      console.error(err);
      nav("/student/exams");
    }
  };

  const debouncedSave = useDebouncedCallback(async (payload, flagsSet, visitedSet, perQ = undefined) => {
    if (!autosaveEnabledRef.current) return;
    if (!attempt || norm(attempt.status) !== "in-progress" || !online) return;
    try {
      setSaving(true);
      const ref = doc(db,"schools",schoolId,"exams",examId,"attempts",uid);
      const update = {
        answers: payload,
        flags: Array.from(flagsSet || []),
        visited: Array.from(visitedSet || []),
        lastSavedAt: serverTimestamp(),
        answeredCount: Object.values(payload || {}).filter(a => (Array.isArray(a.value) ? a.value.length : String(a.value || "").trim().length)).length,
      };
      if (perQ) update.perQuestionSec = perQ;
      await setDoc(ref, update, { merge: true });
      setLastSavedAt(new Date());
    } catch (err) {
      console.error(err);
      toast.error("Autosave failed.", { containerId: TOAST_ID, autoClose: 1500 });
    } finally { setSaving(false); }
  }, 500);

  const setAnswer = (qid, type, value) => {
    setAnswers(prev => {
      const next = { ...prev, [qid]: { type, value } };
      debouncedSave(next, flags, visited, perQSec);
      return next;
    });
  };

  const toggleFlag = () => {
    const q = questions[qIndex]; if (!q) return;
    setFlags(prev => {
      const next = new Set(prev);
      if (next.has(q.id)) next.delete(q.id); else next.add(q.id);
      debouncedSave(answers, next, visited, perQSec);
      return next;
    });
  };

  const clearCurrent = () => {
    const q = questions[qIndex]; if (!q) return;
    setAnswers(prev => {
      const next = { ...prev }; delete next[q.id];
      debouncedSave(next, flags, visited, perQSec);
      return next;
    });
  };

  // per-question timer control
  const startQTimer = (qid) => { qTimerRef.current = { qid, startedAt: Date.now() }; };
  const stopQTimer  = (save = true) => {
    const { qid, startedAt } = qTimerRef.current || {};
    if (!qid || !startedAt) return;
    const delta = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
    setPerQSec(prev => {
      const next = { ...prev, [qid]: (prev[qid] || 0) + delta };
      if (save) debouncedSave(answers, flags, visited, next);
      return next;
    });
    qTimerRef.current = { qid: null, startedAt: null };
  };

  // mark visited + manage per-question timer when index changes
  useEffect(() => {
    const q = questions[qIndex];
    stopQTimer();
    if (!q) return;
    startQTimer(q.id);

    setVisited(prev => {
      if (prev.has(q.id)) return prev;
      const next = new Set(prev); next.add(q.id);
      debouncedSave(answers, flags, next, perQSec);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qIndex, questions.length]);

  /* -------------------- render helpers -------------------- */
  const studentTimer = useMemo(() => computeStudentTimer(exam, attempt), [exam, attempt, tick]);

  const timeProgress = useMemo(() => {
    const isMock = norm(exam?.examMode) === "mock";
    if (isMock) return { pct: 0, label: "Anytime", mode: "anytime" };

    if (attempt && norm(attempt.status)==="in-progress" && (studentTimer.totalSec>0)) {
      const pct = Math.max(0, Math.min(100, Math.round((studentTimer.leftSec / studentTimer.totalSec) * 100)));
      return { pct, label: studentTimer.label, mode: studentTimer.mode };
    }
    const sched = computeSchedule(exam);
    if (!sched.valid || sched.totalSec===0) return { pct: 0, label: "", mode: "idle" };
    const pct = Math.max(0, Math.min(100, Math.round((sched.leftSec / sched.totalSec) * 100)));
    const d = dayjs.duration(sched.leftSec,"seconds");
    const hh = String(Math.floor(d.asHours())).padStart(2,"0");
    const mm = String(d.minutes()).padStart(2,"0");
    const ss = String(d.seconds()).padStart(2,"0");
    return { pct, label: `${hh}:${mm}:${ss}`, mode: sched.state };
  }, [exam, attempt, studentTimer, tick]);

  const current = questions[qIndex] || null;

  // Subject -> Section question map
  const mapBySubjectSection = useMemo(() => {
    const m = new Map();
    questions.forEach((q, i) => {
      const subject = q.subject || "General";
      const section = q.sectionKey || "";
      if (!m.has(subject)) m.set(subject, new Map());
      const secMap = m.get(subject);
      if (!secMap.has(section)) secMap.set(section, []);
      const a = answers[q.id];
      const isAns = !!(a && (Array.isArray(a.value) ? a.value.length : String(a.value || "").trim() !== ""));
      const status = flags.has(q.id) ? "flagged" : (isAns ? "answered" : (visited.has(q.id) ? "visited" : "blank"));
      secMap.get(section).push({ index: i, qid: q.id, status });
    });
    for (const [, secMap] of m.entries()) for (const [, list] of secMap.entries()) list.sort((a, b) => a.index - b.index);
    return m;
  }, [questions, answers, flags, visited]);

  const answeredCount = useMemo(() => {
    return questions.reduce((acc, q) => {
      const a = answers[q.id];
      if (!a) return acc;
      if (Array.isArray(a.value)) return acc + (a.value.length > 0 ? 1 : 0);
      return acc + (String(a.value || "").trim() !== "" ? 1 : 0);
    }, 0);
  }, [questions, answers]);

  const goto = (i) => { const n = questions.length; if (!n) return; setQIndex(Math.max(0, Math.min(n-1, i))); };

  const renderOptions = (q) => {
    const qType = norm(q.questionType);

    if (qType === "assertion_reason") {
      const choices = [
        { key: "A", label: "Both statements are true, and Reason is the correct explanation of Assertion." },
        { key: "B", label: "Both statements are true, but Reason is NOT the correct explanation of Assertion." },
        { key: "C", label: "Assertion is true, but Reason is false." },
        { key: "D", label: "Assertion is false." }
      ];
      return (
        <>
          <div className="ar-block"><div className="ar-title">Assertion</div><div className="ar-html" dangerouslySetInnerHTML={{ __html: q.assertionHtml || "" }} /></div>
          <div className="ar-block"><div className="ar-title">Reason</div><div className="ar-html" dangerouslySetInnerHTML={{ __html: q.reasonHtml || "" }} /></div>
          <Form className="mt-2">
            {choices.map((c)=>(
              <Form.Check
                key={`${q.id}_AR_${c.key}`}
                type="radio" name={`q_${q.id}`} id={`${q.id}_AR_${c.key}`}
                className="exam-option"
                label={<span>{c.key}. {c.label}</span>}
                checked={answers[q.id]?.value === c.key}
                onChange={() => setAnswer(q.id, "assertion_reason", c.key)}
              />
            ))}
          </Form>
        </>
      );
    }

    if (qType === "match") {
      const n = Number(q.matchPairCount || 0);
      const hasLegacy = Array.isArray(q.matchPairs) && q.matchPairs.some(r => r?.leftHtml || r?.rightHtml);
      const opts = (q.matchOptions || []).slice(0,4);
      const ans = answers[q.id]?.value; // 0..3
      return (
        <>
          {hasLegacy && (
            <div className="mtf-table-wrap">
              <table className="mtf-table">
                <thead><tr><th>List-I</th><th>List-II</th></tr></thead>
                <tbody>
                  {(q.matchPairs || []).filter(r=>r.leftHtml||r.rightHtml).map((r,i)=>(
                    <tr key={i}>
                      <td dangerouslySetInnerHTML={{ __html: r.leftHtml || "" }} />
                      <td dangerouslySetInnerHTML={{ __html: r.rightHtml || "" }} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="text-muted small mb-2">{n ? `Pairs: ${n} (labels: a… / i…)` : ""}</div>
          <Form>
            {opts.map((arr, idx) => (
              <Form.Check
                key={`${q.id}_MTF_${idx}`}
                type="radio" name={`q_${q.id}`} id={`${q.id}_MTF_${idx}`}
                className="exam-option"
                label={<span>Option {idx+1}: {formatMappingText(arr)}</span>}
                checked={ans === idx}
                onChange={() => setAnswer(q.id, "match", idx)}
              />
            ))}
          </Form>
        </>
      );
    }

    const opts = Array.isArray(q.options) ? q.options : (qType === "true_false" ? ["True","False"] : []);
    const ans  = answers[q.id]?.value;

    if (qType === "mcq" || qType === "true_false") {
      return (
        <Form>
          {opts.map((opt, i) => {
            const labelHTML = optText(opt, i);
            const val       = optValue(opt, i);
            const checked   = (ans === val);
            return (
              <Form.Check
                key={`${q.id}_${i}`} type="radio" name={`q_${q.id}`} id={`${q.id}_${i}`}
                label={<span dangerouslySetInnerHTML={{ __html: labelHTML }} />}
                checked={!!checked} onChange={() => setAnswer(q.id, qType, val)}
                className="exam-option"
              />
            );
          })}
        </Form>
      );
    }

    if (qType === "multi_correct") {
      const cur = Array.isArray(ans) ? ans : [];
      const toggle = (v) => {
        const next = cur.includes(v) ? cur.filter(x=>x!==v) : [...cur, v];
        setAnswer(q.id, qType, next);
      };
      return (
        <Form>
          {opts.map((opt, i) => {
            const labelHTML = optText(opt, i);
            const val       = optValue(opt, i);
            return (
              <Form.Check
                key={`${q.id}_${i}`} type="checkbox" id={`${q.id}_${i}`}
                label={<span dangerouslySetInnerHTML={{ __html: labelHTML }} />}
                checked={cur.includes(val)} onChange={() => toggle(val)}
                className="exam-option"
              />
            );
          })}
        </Form>
      );
    }

    if (qType === "case_based") {
      const subs = Array.isArray(q.caseSubQs) ? q.caseSubQs : [];
      const cur = (answers[q.id]?.value && typeof answers[q.id].value === "object") ? answers[q.id].value : {};
      const setSubAns = (sidx, key) => { const next = { ...cur, [sidx]: key }; setAnswer(q.id, "case_based", next); };
      return (
        <>
          {q.passageHtml && (
            <div className="ar-block" style={{ marginBottom: 12 }}>
              <div className="ar-title">Passage</div>
              <div dangerouslySetInnerHTML={{ __html: q.passageHtml }} />
            </div>
          )}
          {subs.map((sq, sidx) => (
            <div key={sq.id || sidx} className="mb-3">
              <div className="fw-semibold mb-1">Q{subs.length > 1 ? `${sidx + 1}` : ""}</div>
              <div className="mb-2" dangerouslySetInnerHTML={{ __html: sq.questionHtml || "" }} />
              <Form>
                {(sq.options || []).map((o, i) => (
                  <Form.Check
                    key={`${q.id}_CB_${sidx}_${o.key}`}
                    type="radio"
                    name={`${q.id}_CB_${sidx}`}
                    id={`${q.id}_CB_${sidx}_${o.key}`}
                    className="exam-option"
                    label={<span dangerouslySetInnerHTML={{ __html: optText(o, i) }} />}
                    checked={cur[sidx] === o.key}
                    onChange={() => setSubAns(sidx, o.key)}
                  />
                ))}
              </Form>
            </div>
          ))}
        </>
      );
    }

    return (
      <Form.Control
        as="textarea" rows={7}
        value={String(ans || "")}
        onChange={(e) => setAnswer(q.id, "descriptive", e.target.value)}
        placeholder="Type your answer here…"
      />
    );
  };

    /* -------------------- UI -------------------- */
  return (
    <div className="exam-root" data-theme={theme} data-live={isLiveExam ? "1" : "0"}>
      <ToastContainer
        containerId={TOAST_ID}
        position="bottom-right"
        newestOnTop
        closeOnClick={false}
        pauseOnFocusLoss
        draggable
        theme={theme === "dark" ? "dark" : "light"}
        limit={4}
      />

      {loading ? (
        <div className="exam-loading"><Spinner animation="border" size="sm" /> Loading exam…</div>
      ) : !exam ? (
        <div className="exam-loading"><Alert variant="danger" className="mb-0">Exam not found.</Alert></div>
      ) : norm(profile?.status) === "inactive" ? (
        <div className="exam-loading"><Alert variant="warning" className="mb-0">Your account is inactive. You cannot write exams.</Alert></div>
      ) : (
        <>
          {/* Top bar */}
          <div className="exam-topbar">
            <div className="left">
              <div className="title">{exam.name || "Exam"}</div>
              <div className="meta">
                {exam.date ? dayjs(exam.date).format("DD MMM YYYY") : "—"}
                {exam.startTime && exam.endTime ? <> • {exam.startTime}–{exam.endTime}</> : null}
                {exam.durationMins ? <> • {exam.durationMins} mins</> : null}
                {" "}• <Badge bg="light" text="dark">{profile?.className || "—"}</Badge>
                {" "}<Badge bg="light" text="dark">{profile?.section || "Whole class"}</Badge>
              </div>
            </div>
            <div className="right">
              <Badge bg={
                timeProgress.mode === "running"  ? "success" :
                timeProgress.mode === "live"     ? "success" :
                timeProgress.mode === "anytime"  ? "dark"    :
                timeProgress.mode === "upcoming" ? "info"    :
                timeProgress.mode === "time-up"  ? "danger"  : "secondary"
              }>
                {timeProgress.mode === "running"  ? `Time left: ${timeProgress.label}` :
                 timeProgress.mode === "live"     ? `Window left: ${timeProgress.label}` :
                 timeProgress.mode === "anytime"  ? "Anytime" :
                 timeProgress.mode === "upcoming" ? "Starts soon" :
                 timeProgress.mode === "time-up"  ? "Time up" : "Ended"}
              </Badge>

              <div className="save">{saving ? "Saving…" : lastSavedAt ? `Saved ${dayjs(lastSavedAt).format("h:mm:ss A")}` : "—"}</div>

              {/* Theme toggle */}
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={toggleTheme}
                title={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
              >
                {theme === "dark" ? <i className="bi bi-sun" /> : <i className="bi bi-moon" />}
              </Button>

              <div className="d-flex gap-2">
                <Button size="sm" variant="outline-primary" onClick={() => setShowMap(true)} disabled={questions.length===0} title="Question map">
                  <i className="bi bi-grid-3x3-gap" />
                </Button>
                <Button
                  size="sm"
                  variant="outline-danger"
                  disabled={locked || !(attempt && norm(attempt.status) === "in-progress")}
                  onClick={() => setShowConfirm(true)}
                >
                  Submit
                </Button>
              </div>
            </div>
          </div>

          {/* Start gate — allow for mock anytime OR live window */}
          {(!attempt || norm(attempt.status) !== "in-progress") &&
           (norm(exam?.examMode) === "mock" || sched.state === "live") && (
            <div className="exam-startbar">
              <Button
                variant="secondary"
                onClick={() => { if (attempt && norm(attempt.status)==="in-progress") setShowLeaveConfirm(true); else nav("/student/exams"); }}
              >
                Back
              </Button>
              <Button
                variant="primary"
                onClick={beginAttempt}
                disabled={!eligible || starting || locked}
                title={eligible ? "Start your exam" : "Not available now"}
              >
                {starting ? "Starting…" : "Begin"}
              </Button>
            </div>
          )}

          {attempt && norm(attempt.status) === "in-progress" && (
            <div className="exam-content">
              {/* Question position + badges */}
              <div className="qmeta">
                <span className="qpos">Q {questions.length ? qIndex + 1 : 0} / {questions.length}</span>
                <div className="badges">
                  {current?.subject ? <Badge bg="light" text="dark">{current.subject}</Badge> : null}
                  {"marks" in (current || {}) ? <Badge bg="success">{current.marks} marks</Badge> : null}
                  {current?.sectionKey ? <Badge bg="info" text="dark">Sec {current.sectionKey}</Badge> : null}
                  {current && flags.has(current.id) && <Badge bg="warning" text="dark">Flagged</Badge>}
                </div>
              </div>

              {/* SCROLLABLE QUESTION AREA */}
              <Card className="shadow-sm border-0 exam-safe bg-body text-body">
                <Card.Body>
                  {!current ? (
                    <div className="text-muted">No questions.</div>
                  ) : (
                    <>
                      <div className="stem-wrap" dangerouslySetInnerHTML={{ __html: current.questionHtml || "" }} />
                      {renderOptions(current)}
                    </>
                  )}
                </Card.Body>

                {/* STICKY PROGRESS */}
                <div className="progress-sticky">
                  <ProgressBar
                    now={100 - timeProgress.pct}
                    label={timeProgress.label}
                    style={{ height: 18, borderRadius: 10 }}
                    variant={
                      timeProgress.mode === "running"
                        ? (timeProgress.pct > 66 ? "success" : timeProgress.pct > 33 ? "warning" : "danger")
                        : timeProgress.mode === "live" ? "success"
                        : timeProgress.mode === "anytime" ? "secondary"
                        : timeProgress.mode === "upcoming" ? "info"
                        : "secondary"
                    }
                  />
                </div>
              </Card>

              {/* FLOATING bottom navigation */}
              <div className="floating-nav">
                <div className="pill">
                  <Button size="sm" variant="outline-secondary" onClick={() => goto(qIndex - 1)} disabled={qIndex<=0} title="Previous">
                    <i className="bi bi-chevron-left" /> Previous
                  </Button>
                  <button
                    type="button"
                    className="icon-circle"
                    onClick={() => setShowMap(true)}
                    title="Question Map"
                    aria-label="Open question map"
                    disabled={!questions.length}
                  >
                    <i className="bi bi-grid-3x3-gap" />
                  </button>
                  <div className="sep" />
                  <Button size="sm" variant="outline-secondary" onClick={() => goto(qIndex + 1)} disabled={qIndex >= questions.length - 1} title="Next">
                    Next <i className="bi bi-chevron-right" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {attempt && norm(attempt.status) === "submitted" && (
            <div className="exam-loading">
              <Alert variant="success" className="mb-0">
                Your attempt is <b>submitted</b>.
                {attempt.submittedAt ? <> Submitted at: <b>{dayjs(toJS(attempt.submittedAt)).format("DD MMM, h:mm A")}</b></> : null}
              </Alert>
            </div>
          )}
        </>
      )}

      {/* Confirm submit */}
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton><Modal.Title>Submit exam?</Modal.Title></Modal.Header>
        <Modal.Body>
          You have answered <b>{answeredCount}</b> out of <b>{questions.length}</b> questions.
          You can’t change answers after submitting.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirm(false)}>Cancel</Button>
          <Button variant="danger" onClick={() => { setShowConfirm(false); autoSubmit("student-submit"); }}>Submit</Button>
        </Modal.Footer>
      </Modal>

      {/* Confirm leave without submitting */}
      <Modal show={showLeaveConfirm} onHide={() => setShowLeaveConfirm(false)} centered>
        <Modal.Header closeButton><Modal.Title>Leave this exam?</Modal.Title></Modal.Header>
        <Modal.Body>
          If you go back now, this exam will <b>not</b> be submitted. Unsaved changes may be lost.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLeaveConfirm(false)}>Stay</Button>
          <Button
            variant="danger"
            onClick={() => {
              autosaveEnabledRef.current = false;
              leavingRef.current = true;
              setShowLeaveConfirm(false);
              if (popHandlerRef.current) window.removeEventListener("popstate", popHandlerRef.current);
              nav("/student/exams");
            }}
          >
            Leave without submitting
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Question Map */}
      <Modal
        show={showMap}
        onHide={() => setShowMap(false)}
        size="lg"
        centered
        dialogClassName="qmap-modal"
        contentClassName="qmap-modal-content bg-body text-body border-0 shadow-lg"
        backdropClassName="qmap-backdrop"
      >
        <Modal.Header closeButton><Modal.Title>Question Map</Modal.Title></Modal.Header>
        <Modal.Body>
          {questions.length === 0 ? (
            <div className="text-muted">No questions.</div>
          ) : (
            <div className="qmap">
              {Array.from(mapBySubjectSection.entries()).map(([subject, secMap]) => (
                <div key={subject} className="qmap-section">
                  <div className="qmap-subj">{subject}</div>
                  {Array.from(secMap.entries()).map(([sectionKey, list]) => (
                    <div key={`${subject}-${sectionKey}`} className="qmap-block">
                      <div className="qmap-sec">Section: {sectionKey || "General"}</div>
                      <div className="qmap-grid">
                        {list.map((it) => {
                          const variant =
                            it.status === "flagged"   ? "warning"   :
                            it.status === "answered"  ? "success"   :
                            it.status === "visited"   ? "secondary" : "light";
                          const title =
                            it.status === "flagged"   ? "Flagged"   :
                            it.status === "answered"  ? "Answered"  :
                            it.status === "visited"   ? "Visited"   : "Not visited";
                          return (
                            <Button
                              key={it.qid}
                              size="sm"
                              variant={variant}
                              title={`${title} — Q#${it.index + 1}`}
                              onClick={() => { setShowMap(false); goto(it.index); }}
                              className="qmap-cell"
                            >
                              {it.index + 1}
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
              <div className="qmap-legend">
                <span className="legend-chip answered">Answered</span>
                <span className="legend-chip flagged">Flagged</span>
                <span className="legend-chip visited">Visited</span>
                <span className="legend-chip blank">Not visited</span>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowMap(false)}>Close</Button></Modal.Footer>
      </Modal>

      {/* Screenshot shield (brief overlay when PrintScreen is pressed) */}
      {screenshotShield && (
        <div className="screen-shield">
          <div className="shield-msg">Screenshots are not allowed</div>
        </div>
      )}

      {/* Lock overlay (blocks all interaction if locked) */}
      {locked && (
        <div className="lock-overlay">
          <div className="lock-box">
            <div className="lock-title">Exam Locked</div>
            <div className="lock-note">Please contact your invigilator.</div>
          </div>
        </div>
      )}

      {/* THEME VARIABLES + STYLES */}
      <style>{`
        /* Accent derived from brand */
        .exam-root {
          --accent: ${brand.primary};
          --accent-weak: ${hexToRgba(brand.primary, 0.18)};
          --accent-soft: ${hexToRgba(brand.primary, 0.08)};
        }

        /* Scoped theme (light/dark) */
        .exam-root[data-theme="light"] {
          --bg: #ffffff;
          --text: #111827;
          --muted: #6b7280;
          --border: rgba(0,0,0,.08);
          --soft-border: rgba(0,0,0,.06);
          --card: #ffffff;
          --cardTint: ${hexToRgba(brand.primary, 0.04)};
          --shadow: 0 8px 30px rgba(0,0,0,.12);
          --ring: ${hexToRgba(brand.primary, 0.25)};
        }
        .exam-root[data-theme="dark"] {
          --bg: #0b0f19;
          --text: #e5e7eb;
          --muted: #9ca3af;
          --border: rgba(255,255,255,.12);
          --soft-border: rgba(255,255,255,.08);
          --card: #111827;
          --cardTint: ${hexToRgba(brand.primary, 0.10)};
          --shadow: 0 10px 36px rgba(0,0,0,.55);
          --ring: ${hexToRgba(brand.primary, 0.35)};
        }

        body.exam-mode .app-header,
        body.exam-mode .sidebar,
        body.exam-mode header,
        body.exam-mode nav.sidebar { display: none !important; }
        body.exam-mode { overflow: auto; background: var(--bg); }

        .exam-root {
          min-height: 100vh; display: flex; flex-direction: column; color: var(--text); background:
          radial-gradient(1200px 600px at 10% -10%, var(--cardTint), transparent 60%),
          radial-gradient(900px 500px at 100% 0%, ${hexToRgba(brand.primary, 0.06)}, transparent 55%),
          var(--bg);
        }

        .exam-loading { min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 16px; color: var(--text); }

        .exam-topbar {
          position: sticky; top: 0; z-index: 7;
          display: flex; align-items: center; justify-content: space-between;
          padding: 12px 18px; background: var(--card); color: var(--text);
          border-bottom: 1px solid var(--soft-border);
        }
        .exam-topbar .title { font-weight: 700; letter-spacing: .2px; }
        .exam-topbar .meta, .exam-topbar .save { font-size: 12px; color: var(--muted); }
        .exam-topbar .right { display: flex; align-items: center; gap: 10px; }

        .exam-startbar { padding: 12px 16px; border-bottom: 1px solid var(--soft-border); display:flex; gap:8px; background: var(--card); }

        .exam-content { flex: 1; display: grid; grid-template-columns: 1fr; gap: 12px; padding: 12px 16px; overflow: hidden; }

        .qmeta { display: flex; justify-content: space-between; align-items: center; gap: 10px; padding-bottom: 8px; border-bottom: 1px solid var(--soft-border); }
        .qmeta .qpos { font-weight: 700; letter-spacing: .2px; }
        .qmeta .badges { display: flex; flex-wrap: wrap; gap: 6px; }

        .qscroll { flex: 1; min-height: 0; overflow: auto; padding-bottom: 84px; scrollbar-gutter: stable; }
        .exam-safe {
          border-radius: 16px;
          background: linear-gradient(180deg, var(--cardTint) 0%, transparent 60%);
          box-shadow: var(--shadow);
          border: 1px solid var(--soft-border);
        }
        .progress-sticky {
          position: sticky; bottom: 0; left: 0; right: 0; background: var(--card);
          border-top: 1px solid var(--soft-border); padding: 8px 12px;
          border-bottom-left-radius: 16px; border-bottom-right-radius: 16px;
        }

        .stem-wrap p { margin-bottom: 8px; }
        .stem-wrap img {
          display: block; max-width: 100%; height: auto !important;
          margin: .5rem 0 !important; object-fit: contain;
        }

        .exam-option.form-check { margin: 0; }
        .exam-option {
          padding: 10px 12px; border-radius: 12px; background: var(--card);
          border: 1px solid var(--border);
          transition: box-shadow .15s ease, border-color .15s ease, background-color .15s ease;
        }
        .exam-option + .exam-option { margin-top: 6px; }
        .exam-option .form-check-input { transform: scale(1.1); margin-top: 0.35rem; cursor: pointer; }
        .exam-option .form-check-label { white-space: normal; word-break: break-word; line-height: 1.48; color: var(--text); cursor: pointer; }

        .exam-option:has(input:hover), .exam-option:has(label:hover) {
          border-color: var(--accent);
          box-shadow: 0 0 0 3px var(--ring);
          background: var(--accent-soft);
        }
        .exam-option:has(input:focus-visible) {
          border-color: var(--accent);
          box-shadow: 0 0 0 4px var(--ring);
        }
        .exam-option input:checked + label,
        .exam-option input:checked ~ label { font-weight: 600; }

        .ar-block { background: rgba(127,127,127,.08); border:1px solid var(--soft-border); border-radius:12px; padding:12px 14px; margin-bottom:10px; }
        .ar-title { font-weight:700; margin-bottom:6px; }

        .mtf-table-wrap { overflow-x:auto; margin-bottom:8px; }
        .mtf-table { width:100%; border-collapse: collapse; }
        .mtf-table th, .mtf-table td { border:1px solid var(--soft-border); padding:8px; vertical-align:top; color: var(--text); }
        .mtf-table th { background: rgba(127,127,127,.08); }

        .floating-nav { position: fixed; left: 50%; transform: translateX(-50%); bottom: 18px; z-index: 8; }
        .floating-nav .pill {
          display: flex; align-items: center; gap: 10px;
          background: var(--card); border: 1px solid var(--border);
          box-shadow: var(--shadow);
          padding: 8px 10px; border-radius: 999px;
        }
        .floating-nav .sep { width: 1px; height: 22px; background: var(--soft-border); }

        .icon-circle {
          width: 40px; height: 40px; border-radius: 999px;
          border: 1px solid var(--border); background: var(--card);
          color: var(--text);
          display: inline-flex; align-items: center; justify-content: center;
          margin: 0 6px; box-shadow: var(--shadow); cursor: pointer;
          transition: transform .1s ease;
        }
        .icon-circle:active { transform: scale(.96); }
        .icon-circle:disabled { opacity: .6; cursor: not-allowed; }
        .icon-circle i { font-size: 1.05rem; line-height: 1; }

        .qmap-modal .modal-content {
          --bs-modal-bg: var(--card) !important;
          color: var(--text);
          border: 1px solid var(--soft-border);
          box-shadow: var(--shadow);
        }
        .qmap-modal .modal-header, .qmap-modal .modal-footer {
          background: var(--card);
          border-color: var(--soft-border);
        }
        .qmap-modal .modal-body { background: var(--card); }
        .modal-backdrop { background-color: #000; }
        .modal-backdrop.show { opacity: .35; }

        .qmap { display: flex; flex-direction: column; gap: 14px; }
        .qmap-section { padding: 10px 12px; border: 1px solid var(--soft-border); border-radius: 12px; background: var(--card); }
        .qmap-subj { font-weight: 700; margin-bottom: 8px; }
        .qmap-sec { color: var(--muted); font-size: 12px; margin-bottom: 6px; }
        .qmap-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(42px, 1fr)); gap: 8px; }
        .qmap-cell { width: 100%; min-width: 42px; height: 42px; padding: 0; border-radius: 10px !important; font-weight: 600; }

        .qmap-legend {
          display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
          margin-top: 8px; border-top: 1px dashed var(--soft-border); padding-top: 10px;
        }
        .legend-chip {
          font-size: 12px; padding: 6px 10px; border-radius: 999px; border: 1px solid var(--soft-border);
          background: var(--card);
        }
        .legend-chip.answered { background: ${hexToRgba("#22c55e", 0.18)}; border-color: ${hexToRgba("#22c55e", 0.35)}; }
        .legend-chip.flagged  { background: ${hexToRgba("#f59e0b", 0.18)}; border-color: ${hexToRgba("#f59e0b", 0.35)}; }
        .legend-chip.visited  { background: ${hexToRgba("#6b7280", 0.18)}; border-color: ${hexToRgba("#6b7280", 0.35)}; }
        .legend-chip.blank    { background: ${hexToRgba("#d1d5db", 0.3)};  border-color: ${hexToRgba("#9ca3af", 0.4)}; }

        @media (max-width: 992px) {
          .exam-topbar { gap: 8px; }
          .exam-topbar .right { gap: 8px; flex-wrap: wrap; justify-content: flex-end; }
        }
        @media (max-width: 768px) {
          .qscroll { padding-bottom: 108px; }
          .exam-topbar .title { font-size: 1rem; }
          .exam-topbar .meta { font-size: 11px; }
          .floating-nav .pill { gap: 8px; padding: 8px; }
          .icon-circle { width: 38px; height: 38px; }
        }
        @media (max-width: 420px) {
          .qmap-grid { grid-template-columns: repeat(auto-fill, minmax(36px, 1fr)); gap: 6px; }
          .qmap-cell { min-width: 36px; height: 36px; border-radius: 8px !important; font-size: 12px; }
          .exam-option { padding: 9px 10px; border-radius: 10px; }
        }

        /* Bridge custom vars → Bootstrap vars */
        .exam-root {
          --bs-body-bg: var(--bg);
          --bs-body-color: var(--text);
          --bs-card-bg: var(--card);
          --bs-border-color: var(--soft-border);
        }
        .exam-safe, .exam-safe .card-body { background: var(--card); color: var(--text); }
        .stem-wrap { color: var(--text); }
        .exam-root[data-theme="dark"] .stem-wrap [style*="color:#000"] { color: var(--text) !important; }

        + /* 🔒 Anti-copy only during LIVE exams */
 .exam-root[data-live="1"],
 .exam-root[data-live="1"] * { user-select: none; -webkit-user-select: none; }
 .exam-root[data-live="1"] .exam-option input,
 .exam-root[data-live="1"] .exam-option textarea,
 .exam-root[data-live="1"] .exam-option [contenteditable="true"] {
   user-select: text; -webkit-user-select: text;
 }

        /* Screenshot shield */
        .screen-shield {
          position: fixed; inset: 0; background: rgba(0,0,0,.9); z-index: 99998;
          display: grid; place-items: center;
        }
        .screen-shield .shield-msg {
          color: #fff; font-weight: 700; font-size: 20px;
          padding: 10px 16px; border: 2px solid #fff; border-radius: 10px;
        }

        /* Lock overlay */
        .lock-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,.65);
          z-index: 99999; display: grid; place-items: center; backdrop-filter: blur(2px);
        }
        .lock-box {
          background: #fff; color: #111; border-radius: 14px;
          padding: 18px 20px; min-width: 260px; text-align: center;
          box-shadow: 0 14px 42px rgba(0,0,0,.35);
        }
        .lock-title { font-weight: 800; font-size: 18px; margin-bottom: 4px; }
        .lock-note  { color: #555; }
      `}</style>
    </div>
  );
}
